import { ArrowLeft, Filter } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  SEPARATION_DATA_PROVENANCE,
  SEPARATION_MODE_OPTIONS,
  SEPARATION_YEAR_MODES,
  type SeparationMode,
} from './separationPlanner';
import { SeparationPlannerMixture } from './SeparationPlannerMixture';
import { SeparationPlannerWater } from './SeparationPlannerWater';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

export function SeparationPlannerLabPage() {
  const year = useChemistryToolYear();
  const [manualMode, setManualMode] = useState<SeparationMode | null>(null);
  const mode = manualMode ?? modeForYear(SEPARATION_YEAR_MODES, year);

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'separation-planner', state: { manualMode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-sunshine p-6 shadow-card-soft lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Filter className="h-4 w-4" aria-hidden="true" /> Matter &amp; change
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Separation Planner
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Plan a multi-step separation. The order matters: each step uses one property, and a
              step that works on its own can fail at the wrong point.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Explore tool · Year 7+
          </div>
        </div>
        <div className="mt-6 grid max-w-xl grid-cols-2 rounded-full bg-white/75 p-1" aria-label="Choose a mode">
          {SEPARATION_MODE_OPTIONS.map((option) => (
            <button
              key={option.mode}
              type="button"
              aria-pressed={mode === option.mode}
              onClick={() => setManualMode(option.mode)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option.mode ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      {mode === 'mixture' ? <SeparationPlannerMixture /> : <SeparationPlannerWater />}

      <section className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6" aria-labelledby="separation-sources">
        <h2 id="separation-sources" className="text-lg font-extrabold text-ink">Data and conditions</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{SEPARATION_DATA_PROVENANCE.conditions}</p>
        <details className="mt-3 text-xs font-medium leading-5 text-slate2">
          <summary className="cursor-pointer font-extrabold text-ink">Sources</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {SEPARATION_DATA_PROVENANCE.sources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
          <p className="mt-2">Cases are awaiting teaching review.</p>
        </details>
      </section>

      <footer className="mt-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-sm font-medium leading-6 text-slate2">
        Model boundary: the planner follows one main container, and every step separates perfectly.
        Real separations leave traces behind; a filtrate can carry a little very fine clay, and
        ethanol and water cannot be fully separated by distillation (the distillate tops out near
        95% ethanol). Water treatment stages are idealised and do not model dissolved metals,
        chemicals, taste or dose. Real plants also monitor and test the water. This page is not a
        safety guide for practical work: heating, chemicals and mains water treatment need teacher
        supervision and a risk assessment. Nothing on this page is saved or scored.
      </footer>
    </StudyShell>
  );
}
